import { apiFetch, apiFetchEnvelope, type ListMeta } from "@core/http";
import type { operations } from "@core/types/openapi";

// SPEC-220 §3/§8 criterio 1-2: listado de consumos confirmados, con
// filtros combinables por rango de fechas, comercio, miembro, tipo y
// estado. El contrato también expone `workshopId` (30-prizes-redeemables.yaml
// :1119) pero `portal.redemptions` (screens.json) declara un único filtro de
// "Comercio" — no una segunda entrada de "Taller" — así que este cliente
// envía el valor de ese filtro como `merchantId`. No se inventa acá un
// segundo control que el contrato visual no declara.
type RedemptionsQuery = NonNullable<
  operations["portalListRedemptions"]["parameters"]["query"]
>;
type RedemptionsResponse = NonNullable<
  operations["portalListRedemptions"]["responses"][200]["content"]["application/json"]
>;

export type Redemption = NonNullable<RedemptionsResponse["data"]>[number];

export type RedemptionFilters = Pick<
  RedemptionsQuery,
  "memberId" | "merchantId" | "kind" | "reversed" | "from" | "to"
>;

export interface RedemptionsPage {
  items: Redemption[];
  meta: ListMeta;
}

const base = "/v1/portal/redemptions";

export async function listRedemptions(
  filters: RedemptionFilters,
  cursor?: string,
): Promise<RedemptionsPage> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries({ ...filters, cursor })) {
    if (value === undefined || value === "") continue;
    params.set(key, String(value));
  }
  const suffix = params.size ? `?${params}` : "";
  const envelope = await apiFetchEnvelope<Redemption[]>(`${base}${suffix}`);
  const items = envelope.data ?? [];
  return {
    items,
    meta: envelope.meta ?? { next: null, total: items.length },
  };
}

type ReverseRequestBody =
  operations["portalReverseRedemption"]["requestBody"]["content"]["application/json"];
type ReverseResponse = NonNullable<
  operations["portalReverseRedemption"]["responses"][200]["content"]["application/json"]
>;

export type ReverseRedemptionInput = ReverseRequestBody;
export type ReverseRedemptionResult = NonNullable<ReverseResponse["data"]>;

export const reverseRedemption = (
  id: string,
  input: ReverseRedemptionInput,
  idempotencyKey: string,
) =>
  apiFetch<ReverseRedemptionResult>(
    `${base}/${encodeURIComponent(id)}/reverse`,
    {
      method: "POST",
      body: input,
      idempotencyKey,
    },
  );
